import { assertNonNullable } from '@disy/lib/custom-error';

import type { RootStateOr } from 'cadenza/store/cadenza-store';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorksheetDto } from 'cadenza/workbook/api/worksheet-dto';
import { WorksheetSettingsKey } from 'cadenza/workbook/model/worksheet-settings-key.gen';
import type { QueryExecutionStrategyType } from 'cadenza/workbook/query-execution-strategy-type';
import { getActiveWorksheet } from 'cadenza/workbook/workbook-core-selectors.state';
import { slice } from 'cadenza/workbook/workbook-core-slice.state';
import { getSetting } from 'cadenza/workbook/workbook-settings-util';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';

export function getLiveModeRefreshInterval(worksheet: WorksheetDto) {
  const key = WorksheetSettingsKey.toPersistenceKey('PERIODICAL_QUERY_EXECUTION_INTERVAL');
  const refreshInterval = getSetting<number>(worksheet, key);
  assertNonNullable(refreshInterval);
  return refreshInterval;
}

/**
 * Indicates if the live mode is currently running.
 * @param state
 */
export const isLiveModeRunning = (state: RootStateOr<WorkbookState>) => {
  return slice(state).liveModeState.state === 'running';
};

/**
 * Last change in live mode status with some meta information.
 * @param state
 */
export const getLiveModeState = (state: RootStateOr<WorkbookState>) => {
  return slice(state).liveModeState;
};

export const showLoadingIndicatorInLiveMode = (state: RootStateOr<WorkbookState>) => {
  const worksheet = getActiveWorksheet(state);
  const key = WorksheetSettingsKey.toPersistenceKey('SHOW_LOADING_INDICATORS_IN_LIVE_MODE');
  return getSetting(worksheet, key, true);
};

export const isLiveModeEnabled = (state: RootStateOr<WorkbookState>) => {
  const worksheet = getActiveWorksheet(state);
  const key = WorksheetSettingsKey.toPersistenceKey('QUERY_EXECUTION_STRATEGY');
  return getSetting<QueryExecutionStrategyType>(worksheet, key) === 'periodical';
};

/**
 * Last time a live mode refresh was initiated.
 * @param state
 */
export const getLiveModeLastRefreshTime = (state: RootStateOr<WorkbookState>) => {
  return slice(state).liveModeLastRefreshTime;
};

/**
 * IDs of data views that need a (live mode) refresh for the given viewId.
 *
 * @param state
 * @param viewId
 */
export const getLiveModeDataViewsToRefreshForViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return slice(state).liveModeRefreshEvents.find((event) => event.viewId === viewId)?.dataViewIds;
};
